/**
 * Governance Document Detail Page — M6.2 Phase 3
 * View and edit a single governance document with markdown rendering
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API, useAuth } from '../App';
import QMSLayout from '../components/QMSLayout';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, Shield, Edit2, Save, X, Clock, Eye, CheckCircle2,
  Archive, Loader2, AlertTriangle, User, Calendar, FileText,
  Bot, Database, Lock, PenTool, BookOpen, Tag
} from 'lucide-react';

const CATEGORY_ICONS = {
  ai_governance: { icon: Bot, color: 'text-purple-400', bg: 'bg-purple-500/15' },
  data_integrity: { icon: Database, color: 'text-cyan-400', bg: 'bg-cyan-500/15' },
  privacy: { icon: Lock, color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  scriber_disclosure: { icon: PenTool, color: 'text-amber-400', bg: 'bg-amber-500/15' },
  standards_applicability: { icon: BookOpen, color: 'text-blue-400', bg: 'bg-blue-500/15' }
};

const CATEGORY_LABELS = {
  ai_governance: 'AI Governance',
  data_integrity: 'Data Integrity',
  privacy: 'Privacy',
  scriber_disclosure: 'SCRIBER Disclosure',
  standards_applicability: 'Standards Applicability'
};

const STATUS_CONFIG = {
  draft: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30', icon: Clock, label: 'Draft' },
  under_review: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', icon: Eye, label: 'Under Review' },
  active: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', icon: CheckCircle2, label: 'Active' },
  archived: { bg: 'bg-gray-500/15', text: 'text-gray-400', border: 'border-gray-500/30', icon: Archive, label: 'Archived' }
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' }
];

const GovernanceDocumentPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const { user, isAdmin } = useAuth();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editVersion, setEditVersion] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editOwnerRole, setEditOwnerRole] = useState('');

  useEffect(() => {
    loadDocument();
  }, [documentId]);

  const loadDocument = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/governance/documents/${documentId}`, { credentials: 'include' });
      if (res.status === 403) {
        setError('Access denied. Governance documents are restricted to governance roles.');
        return;
      }
      if (res.status === 404) {
        setError('Document not found.');
        return;
      }
      if (!res.ok) throw new Error('Failed to load document');
      const data = await res.json();
      setDoc(data);
    } catch (err) {
      console.error('Error loading governance document:', err);
      setError('Failed to load governance document.');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    setEditTitle(doc.title);
    setEditStatus(doc.status);
    setEditVersion(doc.version);
    setEditBody(doc.body_markdown);
    setEditOwnerRole(doc.owner_role);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const saveDocument = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/governance/documents/${documentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editTitle,
          status: editStatus,
          version: editVersion,
          body_markdown: editBody,
          owner_role: editOwnerRole,
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to update document');
      }
      const updated = await res.json();
      setDoc(updated);
      setEditing(false);
    } catch (err) {
      console.error('Error saving governance document:', err);
      alert(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const statusConfig = doc ? (STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft) : STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  const categoryConfig = doc ? (CATEGORY_ICONS[doc.category] || { icon: FileText, color: 'text-gray-400', bg: 'bg-gray-500/15' }) : { icon: FileText, color: 'text-gray-400', bg: 'bg-gray-500/15' };
  const CategoryIcon = categoryConfig.icon;

  if (loading) {
    return (
      <QMSLayout title={t('governance:documents.title')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </QMSLayout>
    );
  }

  if (error) {
    return (
      <QMSLayout title={t('governance:documents.title')}>
        <div className="qms-card border-red-500/30">
          <div className="qms-card-body p-8 text-center">
            <AlertTriangle className="mx-auto mb-4 text-red-400" size={48} />
            <h3 className="text-white font-semibold mb-2">Error</h3>
            <p className="text-[#8B95A5] text-sm mb-4">{error}</p>
            <button
              onClick={() => navigate('/governance')}
              className="px-4 py-2 bg-[#36A9E1]/20 text-[#36A9E1] rounded hover:bg-[#36A9E1]/30 text-sm"
              data-testid="back-to-governance-btn"
            >
              Back to Governance Center
            </button>
          </div>
        </div>
      </QMSLayout>
    );
  }

  if (!doc) return null;

  return (
    <QMSLayout title={doc.title}>
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button
          onClick={() => navigate('/governance')}
          className="flex items-center gap-2 text-[#8B95A5] hover:text-white transition-colors text-sm"
          data-testid="back-to-governance-link"
        >
          <ArrowLeft size={16} />
          Back to Governance Center
        </button>

        {isAdmin && !editing && (
          <button
            onClick={startEditing}
            className="flex items-center gap-2 px-4 py-2 bg-[#36A9E1]/20 text-[#36A9E1] rounded hover:bg-[#36A9E1]/30 transition-colors text-sm"
            data-testid="edit-document-btn"
          >
            <Edit2 size={14} />
            Edit Document
          </button>
        )}

        {editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={cancelEditing}
              className="flex items-center gap-2 px-4 py-2 bg-[rgba(255,255,255,0.05)] text-[#8B95A5] rounded hover:bg-[rgba(255,255,255,0.1)] transition-colors text-sm"
              data-testid="cancel-edit-btn"
            >
              <X size={14} />
              Cancel
            </button>
            <button
              onClick={saveDocument}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors text-sm disabled:opacity-50"
              data-testid="save-document-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Changes
            </button>
          </div>
        )}
      </div>

      {/* Draft Notice */}
      {doc.status === 'draft' && (
        <div className="qms-card border-amber-500/30 mb-6">
          <div className="qms-card-body p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={20} />
              <div>
                <h4 className="text-amber-400 font-medium text-sm mb-1">Draft Document — Review Required</h4>
                <p className="text-[#8B95A5] text-xs leading-relaxed">
                  This document is a working draft for internal review. It does not represent final approved corporate policy until formally signed off by designated approvers.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Meta Header */}
      <div className="qms-card mb-6">
        <div className="qms-card-body p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className={`p-3 rounded-lg ${categoryConfig.bg} shrink-0`}>
              <CategoryIcon className={categoryConfig.color} size={28} />
            </div>
            <div className="flex-1 min-w-0">
              {editing ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded px-3 py-2 text-white text-lg font-semibold focus:border-[#36A9E1] focus:outline-none mb-3"
                  data-testid="edit-title-input"
                />
              ) : (
                <h1 className="text-white font-semibold text-lg mb-2" data-testid="document-title">{doc.title}</h1>
              )}

              <div className="flex items-center flex-wrap gap-3 text-xs">
                {/* Status Badge */}
                {editing ? (
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="px-2 py-1 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-xs focus:border-[#36A9E1] focus:outline-none"
                    data-testid="edit-status-select"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-medium ${statusConfig.bg} ${statusConfig.text}`}
                    data-testid="document-status-badge"
                  >
                    <StatusIcon size={12} />
                    {statusConfig.label}
                  </span>
                )}

                {/* Version Badge */}
                {editing ? (
                  <input
                    type="text"
                    value={editVersion}
                    onChange={(e) => setEditVersion(e.target.value)}
                    className="w-20 px-2 py-1 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-xs focus:border-[#36A9E1] focus:outline-none"
                    data-testid="edit-version-input"
                  />
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[rgba(255,255,255,0.05)] text-[#8B95A5]" data-testid="document-version-badge">
                    <Tag size={12} />
                    {doc.version}
                  </span>
                )}

                {/* Category */}
                <span className="inline-flex items-center gap-1 text-[#8B95A5]">
                  <CategoryIcon size={12} className={categoryConfig.color} />
                  {CATEGORY_LABELS[doc.category] || doc.category}
                </span>

                {/* Owner */}
                {editing ? (
                  <input
                    type="text"
                    value={editOwnerRole}
                    onChange={(e) => setEditOwnerRole(e.target.value)}
                    className="w-40 px-2 py-1 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-xs focus:border-[#36A9E1] focus:outline-none"
                    data-testid="edit-owner-input"
                  />
                ) : (
                  <span className="inline-flex items-center gap-1 text-[#8B95A5]">
                    <User size={12} />
                    {doc.owner_role}
                  </span>
                )}

                {/* Dates */}
                <span className="inline-flex items-center gap-1 text-[#8B95A5]">
                  <Calendar size={12} />
                  Updated {new Date(doc.updated_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Body */}
      <div className="qms-card">
        <div className="qms-card-body p-6">
          {editing ? (
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              className="w-full h-[600px] bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded p-4 text-white text-sm font-mono focus:border-[#36A9E1] focus:outline-none resize-y leading-relaxed"
              placeholder="Markdown content..."
              data-testid="edit-body-textarea"
            />
          ) : (
            <div className="prose prose-invert prose-sm max-w-none governance-markdown" data-testid="document-body-content">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="text-xl font-bold text-white mb-4 pb-2 border-b border-[rgba(255,255,255,0.07)]">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-base font-semibold text-white mt-6 mb-3">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-sm font-semibold text-[#B8C7D9] mt-4 mb-2">{children}</h3>,
                  p: ({ children }) => <p className="text-[#8B95A5] text-sm leading-relaxed mb-3">{children}</p>,
                  ul: ({ children }) => <ul className="text-[#8B95A5] text-sm space-y-1 mb-3 list-disc pl-5">{children}</ul>,
                  ol: ({ children }) => <ol className="text-[#8B95A5] text-sm space-y-1 mb-3 list-decimal pl-5">{children}</ol>,
                  li: ({ children }) => <li className="text-[#8B95A5] text-sm leading-relaxed">{children}</li>,
                  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                  em: ({ children }) => <em className="text-[#B8C7D9]">{children}</em>,
                  hr: () => <hr className="border-[rgba(255,255,255,0.07)] my-4" />,
                  table: ({ children }) => <div className="overflow-x-auto mb-4"><table className="w-full text-sm">{children}</table></div>,
                  thead: ({ children }) => <thead className="border-b border-[rgba(255,255,255,0.1)]">{children}</thead>,
                  th: ({ children }) => <th className="text-left text-white font-semibold px-3 py-2 text-xs">{children}</th>,
                  td: ({ children }) => <td className="text-[#8B95A5] px-3 py-2 border-b border-[rgba(255,255,255,0.05)] text-xs">{children}</td>,
                  blockquote: ({ children }) => <blockquote className="border-l-2 border-[#36A9E1] pl-4 my-3 text-[#8B95A5] italic">{children}</blockquote>,
                  code: ({ children }) => <code className="bg-[rgba(255,255,255,0.05)] px-1.5 py-0.5 rounded text-cyan-400 text-xs">{children}</code>,
                }}
              >
                {doc.body_markdown}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </QMSLayout>
  );
};

export default GovernanceDocumentPage;
